import { Activity, ArrowLeftRight, Bell, CheckCircle2, FolderOpen, LayoutGrid, Scale, ShieldCheck, SquareCheck, UserPlus, Users } from "lucide-react";
import { Avatar, CountryBadge } from "./ui";
import { buildAdminDashboard, type DashboardKpi, type NewsItem, type ParticipationFirm, type ReferralRow, type TopMember } from "../lib/adminDashboard";
import type { LexData } from "../lib/types";

const card = "rounded-2xl border border-line bg-surface shadow-soft";
const iconSquare = "grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-tint text-primary";
const sectionKicker = "text-[11px] font-bold uppercase tracking-[0.14em] text-primary";

const kpiIcons: Record<string, typeof Activity> = { participation: Activity, activeReps: Users, openVotes: SquareCheck, exchanges: ArrowLeftRight };
const newsIcons: Record<string, typeof Activity> = { vote: SquareCheck, voteClosed: CheckCircle2, mandate: ShieldCheck, legislative: Scale, exchange: ArrowLeftRight, public: LayoutGrid, project: FolderOpen, member: UserPlus, update: Bell };

export function AdminMissionDashboard({ data }: { data: LexData }) {
  const dashboard = buildAdminDashboard(data);
  return (
    <div className="grid gap-5">
      <div className={sectionKicker}>Progress against LexMosaic goals</div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {dashboard.kpis.map((kpi) => <KpiCard key={kpi.key} kpi={kpi} />)}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
        <div className="grid gap-5">
          <ParticipationCard participation={dashboard.participation} />
          <div className="grid gap-5 lg:grid-cols-2">
            <LatestVoteCard vote={dashboard.latestVote} />
            <TopFirmCard firm={dashboard.topFirm} />
          </div>
          <ExpertiseCard expertise={dashboard.expertise} />
          <TopMembersCard members={dashboard.topMembers} />
        </div>
        <NewsCard news={dashboard.news} />
      </div>
    </div>
  );
}

function Sparkline({ series, tone }: { series: number[]; tone: "green" | "grey" }) {
  const max = Math.max(1, ...series);
  const width = 96;
  const height = 30;
  const step = series.length > 1 ? width / (series.length - 1) : width;
  const points = series.map((value, index) => `${(index * step).toFixed(1)},${(height - (value / max) * (height - 4) - 2).toFixed(1)}`).join(" ");
  const stroke = tone === "green" ? "#2f7d57" : "#b6b0a3";
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible" aria-hidden>
      <polyline points={points} fill="none" stroke={stroke} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function KpiCard({ kpi }: { kpi: DashboardKpi }) {
  const Icon = kpiIcons[kpi.key] ?? Activity;
  const tone = kpi.key === "openVotes" ? "grey" : "green";
  return (
    <div className={`${card} p-4`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-secondary">{kpi.label}</div>
        <span className={iconSquare}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-2 text-[32px] font-semibold leading-none tracking-[-0.02em] text-ink">{kpi.value}</div>
      <div className="mt-3 flex items-end justify-between gap-2">
        {kpi.delta ? (
          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${kpi.delta.direction === "up" ? "bg-goodBg text-good" : "bg-badBg text-bad"}`}>
            {kpi.delta.direction === "up" ? "▲" : "▼"} {kpi.delta.direction === "up" ? "+" : "−"}{kpi.delta.value}{kpi.delta.suffix}
          </span>
        ) : kpi.chip ? (
          <span className="inline-flex items-center rounded-md bg-divider px-2 py-0.5 text-[11px] font-semibold text-secondary">{kpi.chip}</span>
        ) : <span />}
        <Sparkline series={kpi.series} tone={tone} />
      </div>
      <div className="mt-3 text-[12.5px] leading-5 text-secondary">{kpi.caption}</div>
    </div>
  );
}

function Donut({ pct }: { pct: number }) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const filled = (Math.min(100, Math.max(0, pct)) / 100) * circumference;
  return (
    <svg width={132} height={132} viewBox="0 0 132 132" className="shrink-0">
      <circle cx={66} cy={66} r={radius} fill="none" stroke="#eceadf" strokeWidth={16} />
      <circle cx={66} cy={66} r={radius} fill="none" stroke="#7A1C2C" strokeWidth={16} strokeLinecap="round" strokeDasharray={`${filled} ${circumference - filled}`} transform="rotate(-90 66 66)" />
      <text x={66} y={62} textAnchor="middle" className="fill-ink" style={{ fontSize: 24, fontWeight: 600 }}>{pct}%</text>
      <text x={66} y={82} textAnchor="middle" className="fill-[#8A95A8]" style={{ fontSize: 11, letterSpacing: "0.08em" }}>ACTIVE</text>
    </svg>
  );
}


function Bar({ pct, muted }: { pct: number; muted?: boolean }) {
  return (
    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-track">
      <div className={`h-full rounded-full ${muted ? "bg-[#c9c3b5]" : "bg-primary"}`} style={{ width: `${Math.max(2, pct)}%` }} />
    </div>
  );
}

function ParticipationCard({ participation }: { participation: { ratePct: number; activeReps: number; totalReps: number; firms: ParticipationFirm[] } }) {
  return (
    <div className={card}>
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-4">
        <span className={iconSquare}><Activity className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[17px] font-semibold text-ink">Member participation</div>
          <div className="text-[12.5px] text-secondary">{participation.activeReps} of {participation.totalReps} representatives active in the last 30 days</div>
        </div>
        <span className="font-mono text-[12px] text-muted">Last 30 days</span>
      </div>
      <div className="px-5 py-5">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <Donut pct={participation.ratePct} />
          <div className="min-w-0">
            <div className="text-[16px] font-semibold text-ink">Network participation rate</div>
            <p className="mt-1.5 text-[14px] leading-6 text-secondary">Share of representatives who voted, posted, or exchanged expertise in the period, by firm contribution.</p>
          </div>
        </div>
        <div className="mt-5 grid gap-3 border-t border-divider pt-5">
          {participation.firms.length > 0 ? participation.firms.map((firm) => (
            <div key={firm.firmId} className="flex items-center gap-3">
              <CountryBadge code={firm.code} />
              <span className={`w-40 shrink-0 truncate text-[14px] font-medium ${firm.suspended ? "text-muted" : "text-ink"}`}>{firm.name}</span>
              <Bar pct={firm.pct} muted={firm.suspended} />
              <span className={`w-12 shrink-0 text-right text-[13px] font-semibold tabular-nums ${firm.suspended ? "text-muted" : "text-ink"}`}>{firm.pct}%</span>
            </div>
          )) : <div className="text-[13px] text-secondary">No participation recorded yet.</div>}
        </div>
      </div>
    </div>
  );
}

function NewsCard({ news }: { news: NewsItem[] }) {
  return (
    <div className={`${card} xl:sticky xl:top-4`}>
      <div className="flex items-center gap-3 border-b border-line px-5 py-4">
        <span className={iconSquare}><Bell className="h-4 w-4" /></span>
        <div className="flex-1 text-[17px] font-semibold text-ink">Latest news</div>
        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted"><span className="h-1.5 w-1.5 rounded-full bg-good" /> Live</span>
      </div>
      <div className="px-5 py-2">
        {news.length > 0 ? news.map((item) => {
          const Icon = newsIcons[item.iconKey] ?? Bell;
          return (
            <div key={item.id} className="flex gap-3 border-b border-divider py-3 last:border-b-0">
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-divider text-secondary"><Icon className="h-4 w-4" /></span>
              <div className="min-w-0">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted">{item.category}</div>
                <div className="mt-0.5 text-[13.5px] leading-5 text-ink">{item.text}</div>
                <div className="mt-1 font-mono text-[11px] text-muted">{item.timeLabel}</div>
              </div>
            </div>
          );
        }) : <div className="py-6 text-[13px] text-secondary">No recent activity.</div>}
      </div>
    </div>
  );
}

function LatestVoteCard({ vote }: { vote: ReturnType<typeof buildAdminDashboard>["latestVote"] }) {
  if (!vote) return <div className={`${card} grid place-items-center px-5 py-10 text-[13px] text-secondary`}>No votes yet.</div>;
  const total = Math.max(1, vote.forCount + vote.againstCount + vote.abstainCount);
  const quorumMet = vote.quorumReached >= Math.ceil(vote.quorumTotal * 0.66);
  return (
    <div className={card}>
      <div className="flex items-center gap-3 border-b border-line px-5 py-4">
        <span className={iconSquare}><SquareCheck className="h-4 w-4" /></span>
        <div className="flex-1 text-[17px] font-semibold text-ink">Latest vote</div>
        <span className="font-mono text-[12px] text-muted">{vote.closesLabel}</span>
      </div>
      <div className="px-5 py-4">
        <div className="text-[16px] font-semibold leading-snug text-ink">{vote.title}</div>
        <div className="mt-1.5 text-[12.5px] capitalize text-secondary">{vote.meta}</div>
        <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-track">
          <div className="h-full bg-good" style={{ width: `${(vote.forCount / total) * 100}%` }} />
          <div className="h-full bg-bad" style={{ width: `${(vote.againstCount / total) * 100}%` }} />
          <div className="h-full bg-[#c9c3b5]" style={{ width: `${(vote.abstainCount / total) * 100}%` }} />
        </div>
        <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-secondary">
          <span><span className="font-semibold text-good">For {vote.forCount}</span></span>
          <span><span className="font-semibold text-bad">Against {vote.againstCount}</span></span>
          <span>Abstain {vote.abstainCount}</span>
          <span>{vote.notVoted} not yet voted</span>
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-divider pt-3">
          <span className={`inline-flex items-center gap-1.5 text-[13px] font-medium ${quorumMet ? "text-good" : "text-secondary"}`}>{quorumMet ? <CheckCircle2 className="h-4 w-4" /> : null}{vote.quorumReached} of {vote.quorumTotal} quorum {quorumMet ? "reached" : "needed"}</span>
        </div>
      </div>
    </div>
  );
}

function TopFirmCard({ firm }: { firm: ReturnType<typeof buildAdminDashboard>["topFirm"] }) {
  if (!firm) return <div className={`${card} grid place-items-center px-5 py-10 text-[13px] text-secondary`}>No firm activity yet.</div>;
  return (
    <div className={card}>
      <div className="flex items-center gap-3 border-b border-line px-5 py-4">
        <span className={iconSquare}><ShieldCheck className="h-4 w-4" /></span>
        <div className="flex-1 text-[17px] font-semibold text-ink">Most participating firm</div>
        <span className="font-mono text-[12px] text-muted">This quarter</span>
      </div>
      <div className="px-5 py-4">
        <div className="flex items-center gap-3">
          <CountryBadge code={firm.code} />
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-semibold text-ink">{firm.name}</div>
            <div className="text-[12.5px] text-secondary">{firm.jurisdiction} · {firm.category}</div>
          </div>
          <div className="text-right"><span className="text-[22px] font-semibold text-primary">{firm.score}</span><span className="text-[13px] text-muted">/100</span></div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 border-t border-divider pt-4">
          {[{ n: firm.projectsLed, l: "Projects led" }, { n: firm.votesCast, l: "Votes cast" }, { n: firm.referralsMade, l: "Referrals made" }].map((stat) => (
            <div key={stat.l} className="rounded-lg border border-divider bg-canvas px-3 py-2.5">
              <div className="text-[18px] font-semibold tabular-nums text-ink">{stat.n}</div>
              <div className="mt-0.5 text-[11.5px] leading-4 text-secondary">{stat.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReferralLine({ referral }: { referral: ReferralRow }) {
  const tone = referral.tone === "ok" ? "border-goodBorder bg-goodBg text-good" : referral.tone === "warn" ? "border-warnBorder bg-warnBg text-warn" : referral.tone === "danger" ? "border-badBorder bg-badBg text-bad" : "border-line bg-divider text-secondary";
  const dot = referral.tone === "ok" ? "bg-good" : referral.tone === "warn" ? "bg-warn" : referral.tone === "danger" ? "bg-bad" : "bg-muted";
  return (
    <div className="flex items-center gap-3 border-t border-divider py-3 text-[13.5px]">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <CountryBadge code={referral.fromCode} />
        <span className="truncate font-medium text-ink">{referral.fromName}</span>
        <ArrowLeftRight className="h-3.5 w-3.5 shrink-0 text-muted" />
        <CountryBadge code={referral.toCode} />
        <span className="truncate font-medium text-ink">{referral.toName}</span>
      </div>
      <span className="hidden w-36 shrink-0 truncate text-secondary sm:block">{referral.area}</span>
      <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11.5px] font-semibold ${tone}`}><span className={`h-1.5 w-1.5 rounded-full ${dot}`} />{referral.status}</span>
      <span className="w-8 shrink-0 text-right font-mono text-[11px] text-muted">{referral.timeLabel}</span>
    </div>
  );
}

function ExpertiseCard({ expertise }: { expertise: ReturnType<typeof buildAdminDashboard>["expertise"] }) {
  const funnel = [{ n: expertise.introductions, l: "Introductions" }, { n: expertise.engaged, l: "Engaged" }, { n: expertise.mattersOpened, l: "Matters opened" }];
  return (
    <div className={card}>
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-4">
        <span className={iconSquare}><ArrowLeftRight className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[17px] font-semibold text-ink">Expertise exchange</div>
          <div className="text-[12.5px] text-secondary">Interfirm referrals across the network</div>
        </div>
        <span className="font-mono text-[12px] text-muted">Top area · {expertise.topArea}</span>
      </div>
      <div className="px-5 py-5">
        <div className="flex items-stretch gap-2">
          {funnel.map((step, index) => (
            <div key={step.l} className="flex flex-1 items-center gap-2">
              <div className="flex-1 rounded-xl border border-divider bg-canvas px-4 py-3">
                <div className="text-[22px] font-semibold tabular-nums text-ink">{step.n}</div>
                <div className="mt-0.5 text-[12.5px] text-secondary">{step.l}</div>
              </div>
              {index < funnel.length - 1 ? <ArrowLeftRight className="h-4 w-4 shrink-0 text-muted" /> : null}
            </div>
          ))}
        </div>
        <div className="mt-4">
          {expertise.recent.length > 0 ? expertise.recent.map((referral) => <ReferralLine key={referral.id} referral={referral} />) : <div className="border-t border-divider pt-3 text-[13px] text-secondary">No referrals yet.</div>}
        </div>
      </div>
    </div>
  );
}

function TopMembersCard({ members }: { members: TopMember[] }) {
  return (
    <div className={card}>
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-4">
        <span className={iconSquare}><Users className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[17px] font-semibold text-ink">Most participating members</div>
          <div className="text-[12.5px] text-secondary">Ranked by contribution score</div>
        </div>
        <span className="font-mono text-[12px] text-muted">Votes · posts · referrals</span>
      </div>
      <div className="px-5 py-2">
        {members.length > 0 ? members.map((member) => (
          <div key={member.rank} className="flex items-center gap-3 border-b border-divider py-3.5 last:border-b-0">
            <span className="w-5 shrink-0 text-center text-[13px] font-semibold text-muted">{member.rank}</span>
            <Avatar name={member.name} imageUrl={member.avatarUrl} size="sm" shape="rounded" />
            <div className="min-w-0 w-44 shrink-0">
              <div className="truncate text-[14px] font-semibold text-ink">{member.name}</div>
              <div className="truncate text-[12px] text-secondary">{member.firmName}</div>
            </div>
            <div className="hidden shrink-0 items-center gap-3 font-mono text-[12px] text-muted sm:flex">
              <span>✓ {member.votes}</span>
              <span>▤ {member.posts}</span>
              <span>⇄ {member.referrals}</span>
            </div>
            <Bar pct={member.pct} />
            <span className="w-8 shrink-0 text-right text-[14px] font-semibold tabular-nums text-ink">{member.score}</span>
          </div>
        )) : <div className="py-6 text-[13px] text-secondary">No member contributions recorded yet.</div>}
      </div>
    </div>
  );
}
